import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from 'src/store/store';
import { startRequest, updateProgress, endRequest } from '../slices/progressSlice';
import { prepareHeadersHelper } from './apiHelpers';
import { Dispatch } from '@reduxjs/toolkit';

export const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const state = getState() as RootState;
    //ensure these enpoints are not changed in the code, a good case for unit testing
    if (
      endpoint !== 'verifyUserWithJwt' &&
      endpoint !== 'registerUser' &&
      endpoint !== 'getCompanyByDotMc' &&
      endpoint !== 'createProducerFormTrakLead' &&
      endpoint !== 'createPreleadFormTrakLead' &&
      endpoint !== 'getRedirectUrl' &&
      endpoint !== 'checkProducerAvailability'
    ) {
      prepareHeadersHelper(headers, state);
    }
  },
});

//we use this base query for endpoints that do not use the /api/ prefix
const baseQueryWithoutAPI = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL_WITHOUT_API,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const state = getState() as RootState;
    //ensure these enpoints are not changed in the code, a good case for unit testing
    if (endpoint !== 'createProducerFormTrakLead') {
      prepareHeadersHelper(headers, state);
    }
  },
});

const simulateProgress = (dispatch: Dispatch) => {
  let progress = 10;
  dispatch(updateProgress(progress));

  const interval = setInterval(() => {
    if (progress < 90) {
      progress += 20; // Increase progress
      dispatch(updateProgress(progress));
    }
  }, 500);

  return () => {
    clearInterval(interval);
    dispatch(updateProgress(100)); // Ensure progress reaches 100% on cleanup
  };
};

const endpointToBaseQuery: Record<string, typeof baseQuery> = {
  createProducerFormTrakLead: baseQueryWithoutAPI,
  generateSignedUrl: baseQueryWithoutAPI,
  // add more endpoints here later if needed
};

export const baseQueryWithProgress: typeof baseQuery = async (args, api, extraOptions) => {
  const { dispatch, endpoint } = api;

  dispatch(startRequest()); // Start loading
  const clearProgress = simulateProgress(dispatch);
  const selectedBaseQuery = endpointToBaseQuery[endpoint] || baseQuery;
  try {
    const result = await selectedBaseQuery(args, api, extraOptions);
    dispatch(updateProgress(100)); // Ensure it reaches 100%
    return result;
  } catch (error) {
    dispatch(updateProgress(0)); // Reset on error
    throw error;
  } finally {
    clearProgress();
    dispatch(endRequest()); // Stop loading
  }
};
